// Every create, rename, move and delete, as a real form.
//
// One hook owns them so the home screen and a folder screen offer exactly the
// same dialogs with the same wording, and so a screen that wants a new action
// adds it in one place. `open(...)` puts a dialog up; `element` is what the
// screen renders.

import { useCallback, useState } from "react";
import { api } from "../api.js";
import { ConfirmModal, FormModal } from "../ui/Modal.jsx";
import { SelectField, TagField, TextField } from "../ui/Field.jsx";
import { plural } from "../ui/format.js";

const FOLDER_COLORS = [
  { value: "", label: "Honey (default)" },
  { value: "#4c8bf5", label: "Blue" },
  { value: "#3f9e63", label: "Green" },
  { value: "#c4553d", label: "Rust" },
  { value: "#8b5cf6", label: "Violet" },
  { value: "#6b6459", label: "Stone" },
];

/**
 * @param {{folders: object[], onChanged: Function, navigate: Function}} deps
 * @returns {{open: Function, element: JSX.Element|null}}
 */
export const useDialogs = ({ folders = [], onChanged, navigate }) => {
  const [dialog, setDialog] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const open = useCallback((kind, subject = null, extra = {}) => {
    setError(null);
    setBusy(false);
    setDialog({ kind, subject, ...extra });
  }, []);

  const close = useCallback(() => {
    setDialog(null);
    setError(null);
    setBusy(false);
  }, []);

  /** Run a call, keep the dialog open with the message if it fails. */
  const submit = useCallback(
    async (work) => {
      setBusy(true);
      setError(null);
      try {
        const result = await work();
        close();
        await onChanged?.();
        return result;
      } catch (err) {
        setError(err.message);
        setBusy(false);
        return null;
      }
    },
    [close, onChanged],
  );

  const shared = { busy, error, onClose: close };
  let element = null;

  if (dialog?.kind === "newFolder") {
    element = <FolderForm {...shared} folder={null} onSubmit={submit} navigate={navigate} />;
  } else if (dialog?.kind === "renameFolder") {
    element = <FolderForm {...shared} folder={dialog.subject} onSubmit={submit} />;
  } else if (dialog?.kind === "deleteFolder") {
    element = <DeleteFolder {...shared} folder={dialog.subject} onSubmit={submit} navigate={navigate} />;
  } else if (dialog?.kind === "newBoard") {
    element = (
      <BoardForm
        {...shared}
        board={null}
        folders={folders}
        defaultFolderId={dialog.folderId}
        onSubmit={submit}
        navigate={navigate}
      />
    );
  } else if (dialog?.kind === "editBoard") {
    element = (
      <BoardForm {...shared} board={dialog.subject} folders={folders} focus={dialog.focus} onSubmit={submit} />
    );
  } else if (dialog?.kind === "move") {
    element = <MoveBoard {...shared} board={dialog.subject} folders={folders} onSubmit={submit} />;
  } else if (dialog?.kind === "deleteBoard") {
    element = <DeleteBoard {...shared} board={dialog.subject} onSubmit={submit} />;
  }

  return { open, element };
};

// ---- folders ----

const FolderForm = ({ folder, onSubmit, onClose, busy, error, navigate }) => {
  const [name, setName] = useState(folder?.name ?? "");
  const [color, setColor] = useState(folder?.color ?? "");
  const editing = Boolean(folder);

  return (
    <FormModal
      title={editing ? "Rename folder" : "New folder"}
      description={
        editing ? null : "Folders hold boards. One per client or project works well."
      }
      submitLabel={editing ? "Save" : "Create folder"}
      canSubmit={name.trim().length > 0}
      busy={busy}
      error={error}
      onClose={onClose}
      onSubmit={() =>
        onSubmit(async () => {
          if (editing) return api.renameFolder(folder.id, name.trim());
          const { folder: made } = await api.createFolder(name.trim(), color || undefined);
          navigate?.(`/f/${made.id}`);
          return made;
        })
      }
    >
      <TextField
        label="Name"
        value={name}
        maxLength={120}
        placeholder="Architecture"
        onChange={(event) => setName(event.target.value)}
      />
      {editing ? null : (
        <SelectField
          label="Colour"
          value={color}
          options={FOLDER_COLORS}
          onChange={(event) => setColor(event.target.value)}
        />
      )}
    </FormModal>
  );
};

const DeleteFolder = ({ folder, onSubmit, onClose, busy, error, navigate }) => (
  <ConfirmModal
    title={`Delete “${folder.name}”?`}
    description={
      folder.boardCount
        ? `This folder still holds ${plural(folder.boardCount, "board")}. Move or delete them first.`
        : "The folder is empty."
    }
    confirmLabel="Delete folder"
    busy={busy}
    error={error}
    onClose={onClose}
    onConfirm={() =>
      onSubmit(async () => {
        await api.deleteFolder(folder.id);
        navigate?.("/");
      })
    }
  />
);

// ---- boards ----

const BoardForm = ({ board, folders, defaultFolderId, focus, onSubmit, onClose, busy, error, navigate }) => {
  const [name, setName] = useState(board?.name ?? "");
  const [tags, setTags] = useState(board?.tags ?? []);
  const [folderId, setFolderId] = useState(board?.folderId ?? defaultFolderId ?? folders[0]?.id ?? "");
  const editing = Boolean(board);

  return (
    <FormModal
      title={editing ? (focus === "tags" ? "Edit tags" : "Rename board") : "New board"}
      description={
        editing ? null : "An infinite canvas. Everyone can draw on it at the same time."
      }
      submitLabel={editing ? "Save" : "Create board"}
      canSubmit={name.trim().length > 0 && Boolean(folderId)}
      busy={busy}
      error={error}
      onClose={onClose}
      onSubmit={() =>
        onSubmit(async () => {
          if (editing) {
            return api.updateBoard(board.id, { name: name.trim(), tags });
          }
          const { board: made } = await api.createBoard({ name: name.trim(), folderId, tags });
          navigate?.(`/b/${made.id}`);
          return made;
        })
      }
    >
      <TextField
        label="Name"
        value={name}
        maxLength={120}
        placeholder="System overview"
        onChange={(event) => setName(event.target.value)}
      />
      {editing || folders.length <= 1 ? null : (
        <SelectField
          label="Folder"
          value={folderId}
          options={folders.map((f) => ({ value: f.id, label: f.name }))}
          onChange={(event) => setFolderId(event.target.value)}
        />
      )}
      <TagField value={tags} onChange={setTags} autoFocus={focus === "tags"} />
    </FormModal>
  );
};

const MoveBoard = ({ board, folders, onSubmit, onClose, busy, error }) => {
  const others = folders.filter((f) => f.id !== board.folderId);
  const [folderId, setFolderId] = useState(others[0]?.id ?? "");

  if (others.length === 0) {
    return (
      <ConfirmModal
        title="No other folder"
        description="Create another folder first."
        confirmLabel="Close"
        onClose={onClose}
        onConfirm={onClose}
      />
    );
  }

  return (
    <FormModal
      title={`Move “${board.name}”`}
      submitLabel="Move"
      busy={busy}
      error={error}
      onClose={onClose}
      onSubmit={() => onSubmit(() => api.updateBoard(board.id, { folderId }))}
    >
      <SelectField
        label="Move to"
        value={folderId}
        options={others.map((f) => ({ value: f.id, label: f.name }))}
        onChange={(event) => setFolderId(event.target.value)}
      />
    </FormModal>
  );
};

const DeleteBoard = ({ board, onSubmit, onClose, busy, error }) => (
  <ConfirmModal
    title={`Delete “${board.name}”?`}
    description={
      board.elementCount
        ? `${plural(board.elementCount, "element")} will be deleted. Past snapshots stay in S3 and can be restored by hand.`
        : "The board is empty."
    }
    confirmLabel="Delete board"
    busy={busy}
    error={error}
    onClose={onClose}
    onConfirm={() => onSubmit(() => api.deleteBoard(board.id))}
  />
);
